/**
 * Centralized logging utility for Clientelo
 * Provides consistent logging with environment-based levels
 * SECURITY: Automatically sanitizes sensitive data
 */

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

const isDev = process.env.NODE_ENV === 'development';

// Keywords that indicate sensitive data - these will be redacted
const SENSITIVE_KEYWORDS = ['password', 'passwd', 'pwd', 'secret', 'token', 'key', 'credential', 'supabaseKey', 'apiKey', 'accessToken', 'refreshToken'];
const SENSITIVE_PATTERNS = [
  /eyJ[A-Za-z0-9-_=]+\.eyJ[A-Za-z0-9-_=]+\.?[A-Za-z0-9-_.+/=]*/, // JWT tokens
  /[A-Za-z0-9]{32,}/, // Long alphanumeric strings (potential keys/tokens)
];

class Logger {
  private shouldLog(level: LogLevel): boolean {
    if (level === 'error') return true; // Always log errors
    if (level === 'warn') return true; // Always log warnings
    if (level === 'debug') return isDev; // Debug logs only in development
    if (level === 'info') return isDev; // Info logs only in development
    return true;
  }

  /**
   * Sanitizes sensitive data from log arguments
   * Removes passwords, tokens, keys, and other sensitive information
   */
  private sanitize(args: any[]): any[] {
    return args.map(arg => {
      if (arg === null || arg === undefined) return arg;
      
      // Handle strings
      if (typeof arg === 'string') {
        // Check for JWT tokens and other patterns
        for (const pattern of SENSITIVE_PATTERNS) {
          if (pattern.test(arg) && arg.length > 20) {
            return '[REDACTED: Potential token/key]';
          }
        }
        return arg;
      }
      
      // Handle objects - recursively sanitize
      if (typeof arg === 'object' && !Array.isArray(arg)) {
        const sanitized: any = {};
        for (const [key, value] of Object.entries(arg)) {
          const lowerKey = key.toLowerCase();
          
          // Check if key indicates sensitive data
          if (SENSITIVE_KEYWORDS.some(keyword => lowerKey.includes(keyword))) {
            sanitized[key] = '[REDACTED]';
            continue;
          }
          
          // Check if value is a sensitive string
          if (typeof value === 'string') {
            for (const pattern of SENSITIVE_PATTERNS) {
              if (pattern.test(value) && value.length > 20) {
                sanitized[key] = '[REDACTED: Potential token/key]';
                continue;
              }
            }
          }
          
          // Recursively sanitize nested objects
          if (typeof value === 'object' && value !== null) {
            sanitized[key] = this.sanitize([value])[0];
          } else {
            sanitized[key] = value;
          }
        }
        return sanitized;
      }
      
      // Handle arrays
      if (Array.isArray(arg)) {
        return arg.map(item => this.sanitize([item])[0]);
      }
      
      return arg;
    });
  }

  debug(...args: any[]): void {
    if (this.shouldLog('debug')) {
      const sanitized = this.sanitize(args);
      console.debug(...sanitized);
    }
  }

  info(...args: any[]): void {
    if (this.shouldLog('info')) {
      const sanitized = this.sanitize(args);
      console.info('[INFO]', ...sanitized);
    }
  }

  warn(...args: any[]): void {
    if (this.shouldLog('warn')) {
      const sanitized = this.sanitize(args);
      console.warn('[WARN]', ...sanitized);
    }
  }

  error(...args: any[]): void {
    // Always log errors, but sanitize sensitive data
    const sanitized = this.sanitize(args);
    console.error('[ERROR]', ...sanitized);
  }

  /**
   * Group logs for better organization - only shown in development
   */
  group(label: string, fn: () => void): void {
    if (isDev) {
      console.group(label);
      try {
        fn();
      } finally {
        console.groupEnd();
      }
    } else {
      // In production, just execute the function without grouping
      fn();
    }
  }
}

export const logger = new Logger();



